'use client';

import { matchesDevPreviewMember } from '@/lib/dev-preview-member-search';
import { useEffect, useRef, useState } from 'react';

export type DevPreviewMember = {
  id: string;
  name: string;
  memberCode: string;
  isTeacher: boolean;
};

type DevPreviewToolbarProps = {
  members: DevPreviewMember[];
  currentUserId: string | null;
};

export function DevPreviewToolbar({ members, currentUserId }: DevPreviewToolbarProps) {
  const [requestedUserId, setSelectedUserId] = useState(currentUserId ?? '');
  // 認領或切換會員後，目前身分可能不在 預覽清單；顯示與送出必須使用同一個有效選項。
  const selectedUserId = members.some(member => member.id === requestedUserId)
    ? requestedUserId
    : members[0]?.id ?? '';
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const selectedMember = members.find(member => member.id === selectedUserId);
  const filteredMembers = members.filter(member => matchesDevPreviewMember(member, query));
  const activeMember = filteredMembers[activeIndex];

  useEffect(() => {
    if (isOpen) listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen, query]);

  function closeMemberSearch() {
    setIsOpen(false);
    setQuery('');
    setActiveIndex(0);
  }

  function selectMember(member: DevPreviewMember) {
    setSelectedUserId(member.id);
    closeMemberSearch();
  }

  async function switchMember() {
    if (!selectedUserId) return;
    setIsPending(true);
    setError(null);

    try {
      const response = await fetch('/api/dev-preview/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ userId: selectedUserId }),
      });
      const result = await response.json().catch(() => null) as { error?: string } | null;
      if (!response.ok) throw new Error(result?.error || '切換預覽會員失敗');
      window.location.reload();
    } catch (switchError) {
      setError(switchError instanceof Error ? switchError.message : '切換預覽會員失敗');
      setIsPending(false);
    }
  }

  async function endPreview() {
    setIsPending(true);
    await fetch('/api/dev-preview/session', { method: 'DELETE', credentials: 'same-origin' });
    window.location.reload();
  }

  async function startEnrollment() {
    setIsPending(true);
    setError(null);
    try {
      const response = await fetch('/api/dev-preview/enrollment', { method: 'POST', credentials: 'same-origin' });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || '開始首次登入模擬失敗');
      window.location.assign('/home');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '開始首次登入模擬失敗');
      setIsPending(false);
    }
  }

  return (
    <aside className="dev-preview-toolbar" aria-label="桌面開發預覽工具">
      <div className="dev-preview-label">
        <span className="dev-preview-dot" />
        開發模式
      </div>
      <div className="dev-preview-combobox" onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) closeMemberSearch();
      }}>
        <label className="sr-only" htmlFor="dev-preview-member">搜尋模擬會員（姓名、會員編號或角色）</label>
        <input
          id="dev-preview-member"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={isOpen ? 'dev-preview-member-list' : undefined}
          aria-activedescendant={isOpen && activeMember ? `dev-preview-option-${activeMember.id}` : undefined}
          autoComplete="off"
          value={isOpen ? query : selectedMember ? `${selectedMember.name} · ${selectedMember.isTeacher ? '老師' : '學員'}` : ''}
          placeholder={members.length === 0 ? '尚無可預覽會員' : '搜尋姓名、會員編號或角色…'}
          disabled={isPending || members.length === 0}
          onFocus={() => setIsOpen(true)}
          onClick={() => setIsOpen(true)}
          onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); setIsOpen(true); }}
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing) return;
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setIsOpen(true);
              setActiveIndex(index => !isOpen ? 0 : Math.max(0, Math.min(filteredMembers.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))));
            } else if (event.key === 'Enter' && isOpen) {
              event.preventDefault();
              if (activeMember) selectMember(activeMember);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              closeMemberSearch();
            }
          }}
        />
        <span className="dev-preview-chevron" aria-hidden="true">⌄</span>
        {isOpen && <div className="dev-preview-member-menu">
          <div className="dev-preview-member-hint" role="status">{filteredMembers.length ? `${filteredMembers.length} 位會員 · 可搜尋姓名、會員編號、老師或學員` : '找不到符合的會員，請試試其他關鍵字'}</div>
          <ul id="dev-preview-member-list" ref={listRef} role="listbox" aria-label="模擬會員">
            {filteredMembers.map((member, index) => (
              <li key={member.id} id={`dev-preview-option-${member.id}`} role="option"
                aria-selected={member.id === selectedUserId}
                className={index === activeIndex ? 'is-active' : undefined}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectMember(member)}>
                <span className="dev-preview-member-name">{member.name}<small className="dev-preview-member-code">{member.memberCode}</small></span>
                <span className="dev-preview-member-role">{member.isTeacher ? '老師' : '學員'}</span>
                <span className="dev-preview-member-check" aria-hidden="true">{member.id === selectedUserId ? '✓' : ''}</span>
              </li>
            ))}
          </ul>
        </div>}
      </div>
      <button type="button" onClick={switchMember} disabled={isPending || !selectedUserId}>
        {currentUserId ? '切換身分' : '開始預覽'}
      </button>
      {currentUserId && <button type="button" className="dev-preview-end" onClick={endPreview} disabled={isPending}>結束</button>}
      <button type="button" onClick={startEnrollment} disabled={isPending}>模擬首次 LINE 登入</button>
      {error && <span className="dev-preview-error">{error}</span>}
    </aside>
  );
}
